import axios from 'axios';
import { DeliveryQuote } from '../types';

// Use environment variable for production, relative URLs for development
const API_BASE_URL = process.env.REACT_APP_API_URL || ''; // Use full URL in production, relative in dev

// API key from environment variable
const API_KEY = process.env.REACT_APP_API_KEY || '';
const DEBUG_LOGGING = process.env.REACT_APP_DEBUG_LOGGING === 'true';

// Debug logging utility
const debugLog = (message: string) => {
  if (DEBUG_LOGGING) {
    const envInfo = process.env.NODE_ENV === 'development' ? '(dev - using proxy)' : '(prod - same-origin)';
    console.log(`DEBUG: ${message} ${envInfo}`);
  }
};

// API instance with API key for protected endpoints
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to dynamically add API key
api.interceptors.request.use(
  async (config) => {
    // Add API key to headers
    config.headers['X-API-Key'] = API_KEY;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// API instance without API key for auth endpoints
const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// API instance for admin endpoints (Cognito auth + API key)
const adminApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for admin API (Cognito auth + API key)
adminApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    debugLog(`adminApi request token: ${token ? 'present' : 'missing'}`);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      debugLog(`adminApi request headers: ${JSON.stringify(config.headers)}`);
    }
    // Add API key to admin requests as well
    config.headers['X-API-Key'] = API_KEY;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
adminApi.interceptors.response.use(
  (response) => response,
  (error) => {
    debugLog(`adminApi response error: ${error.response?.status} ${JSON.stringify(error.response?.data)}`);
    if (error.response?.status === 401) {
      debugLog('401 error - logging out');
      // Token expired or invalid, clear auth and redirect to login
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Ensure API key is always present
    config.headers['X-API-Key'] = API_KEY;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add request interceptor for auth API (with API key for all endpoints)
authApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Add API key for all auth endpoints including login
    config.headers['X-API-Key'] = API_KEY;
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid, logout user
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Products
export const getProducts = async () => {
  const response = await api.get('/products');
  return response.data;
};

export const getProduct = async (productId: string) => {
  const response = await api.get(`/products/${productId}`);
  return response.data;
};

export const createProduct = async (product: any) => {
  const response = await api.post('/products', product);
  return response.data;
};

export const updateProduct = async (productId: string, product: any) => {
  const response = await api.patch(`/products/${productId}`, product);
  return response.data;
};

export const deleteProduct = async (productId: string) => {
  const response = await api.delete(`/products/${productId}`);
  return response.data;
};

// Bookings
export const getBookings = async (limit: number = 100, nextToken?: string) => {
  const params: any = {};
  if (limit !== 100) params.limit = limit;
  if (nextToken) params.last_evaluated_key = nextToken;
  
  const response = await adminApi.get('/bookings', { params });
  return response.data;
};

export const createBooking = async (booking: any) => {
  const response = await adminApi.post('/bookings', booking);
  return response.data;
};

export const updateBooking = async (bookingId: string, booking: any) => {
  const response = await adminApi.patch(`/bookings/${bookingId}`, booking);
  return response.data;
};

export const deleteBooking = async (bookingId: string) => {
  const response = await adminApi.delete(`/bookings/${bookingId}`);
  return response.data;
};

export const getBooking = async (bookingId: string) => {
  const response = await adminApi.get(`/bookings/${bookingId}`);
  return response.data;
};

export const getFailedBookings = async () => {
  const response = await adminApi.get('/failed-bookings');
  return response.data;
};

export const getPaymentStatus = async (paymentIntentId: string) => {
  const response = await api.get(`/stripe/payment-intent/${paymentIntentId}`);
  return response.data;
};

export const getBatchPaymentStatuses = async (paymentIntentIds: string[]) => {
  const response = await api.post('/stripe/payment-intents/batch', { payment_intent_ids: paymentIntentIds });
  return response.data;
};

export const createPaymentLink = async (bookingId: string, amount: number, description: string) => {
  const response = await api.post('/stripe/create-payment-link', {
    booking_id: bookingId,
    amount,
    description
  });
  return response.data;
};

// Booking Slots
export const getBookingSlots = async (productId: string, startDate: string, endDate: string) => {
  const response = await api.get(`/booking-slots/${productId}`, {
    params: { start_date: startDate, end_date: endDate }
  });
  return response.data;
};

// Availability Check
export const checkAvailability = async (productId: string, startTime: string, endTime: string) => {
  const response = await api.get('/availability', {
    params: {
      product_id: productId,
      start_time: startTime,
      end_time: endTime
    }
  });
  return response.data;
};

// Addons
export const getAddons = async () => {
  const response = await api.get('/addons');
  return response.data;
};

export const createAddon = async (addon: any) => {
  const response = await api.post('/addons', addon);
  return response.data;
};

export const updateAddon = async (addonId: string, addon: any) => {
  const response = await api.patch(`/addons/${addonId}`, addon);
  return response.data;
};

export const deleteAddon = async (addonId: string) => {
  const response = await api.delete(`/addons/${addonId}`);
  return response.data;
};

// Email
export const sendContactEmail = async (emailData: any) => {
  const response = await api.post('/contact', emailData);
  return response.data;
};

export const sendContractEmail = async (bookingId: string, options: {
  include_receipt?: boolean;
  include_terms?: boolean;
  include_payment_link?: boolean;
  include_review_request?: boolean;
  custom_message?: string;
} = {}) => {
  const response = await adminApi.post(`/bookings/${bookingId}/send-contract`, options);
  return response.data;
};

export const previewContractEmail = async (bookingId: string, options: {
  include_receipt?: boolean;
  include_terms?: boolean;
  include_payment_link?: boolean;
  include_review_request?: boolean;
  custom_message?: string;
} = {}) => {
  const response = await adminApi.post(`/bookings/${bookingId}/preview-email`, options);
  return response.data;
};

// Admin - Use authApi for login endpoints
export const adminLogin = async (credentials: any) => {
  const response = await authApi.post('/auth/admin/login', credentials);
  return response.data;
};

export const adminPasswordReset = async (credentials: any) => {
  const response = await authApi.post('/auth/admin/reset-password', credentials);
  return response.data;
};

export const forgotPassword = async (username: string) => {
  const response = await authApi.post('/auth/forgot-password', { username });
  return response.data;
};

export const confirmForgotPassword = async (username: string, confirmationCode: string, newPassword: string) => {
  const response = await authApi.post('/auth/confirm-forgot-password', {
    username,
    confirmation_code: confirmationCode,
    new_password: newPassword
  });
  return response.data;
};

export const getAdminSettings = async () => {
  const response = await adminApi.get('/admin/settings');
  return response.data;
};

export const updateAdminSettings = async (settings: any) => {
  const response = await adminApi.patch('/admin/settings', settings);
  return response.data;
};

// Uploads - New upload system
export const uploadImage = async (file: File, productId: string, category: string = 'product-image', description?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('product_id', productId);
  formData.append('category', category);
  if (description) {
    formData.append('description', description);
  }
  
  // Use adminApi for upload since it requires JWT authentication
  const response = await adminApi.post('/uploads/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Get all uploaded files (admin only)
export const getUploadedFiles = async (productId?: string) => {
  const params: any = {};
  if (productId) {
    params.product_id = productId;
  }
  
  const response = await adminApi.get('/uploads/', { params });
  return response.data;
};

// Get specific file info
export const getUploadedFile = async (fileId: string) => {
  const response = await adminApi.get(`/uploads/${fileId}`);
  return response.data;
};

// Delete specific file
export const deleteUploadedFile = async (fileId: string) => {
  const response = await adminApi.delete(`/uploads/${fileId}`);
  return response.data;
};

// Get all images for a specific product
export const getProductImages = async (productId: string) => {
  const response = await adminApi.get(`/uploads/product/${productId}`);
  return response.data;
};

// Delete all images for a specific product
export const deleteProductImages = async (productId: string) => {
  const response = await adminApi.delete(`/uploads/product/${productId}`);
  return response.data;
};

// Admin User Management
export const getAdminUsers = async () => {
  const response = await adminApi.get('/admin/users');
  return response.data;
};

export const createAdminUser = async (userData: any) => {
  const response = await adminApi.post('/admin/users', userData);
  return response.data;
};

export const deleteAdminUser = async (username: string) => {
  const response = await adminApi.delete(`/admin/users/${username}`);
  return response.data;
};

// Coupons
export const getCoupons = async (activeOnly: boolean = false) => {
  const response = await adminApi.get('/admin/coupons', {
    params: { active_only: activeOnly }
  });
  return response.data;
};

export const getCoupon = async (couponId: string) => {
  const response = await adminApi.get(`/admin/coupons/${couponId}`);
  return response.data;
};

export const createCoupon = async (couponData: any) => {
  const response = await adminApi.post('/admin/coupons', couponData);
  return response.data;
};

export const updateCoupon = async (couponId: string, couponData: any) => {
  try {
    const response = await adminApi.patch(`/admin/coupons/${couponId}`, couponData);
    return response.data;
  } catch (error) {
    console.error('DEBUG: API updateCoupon error:', error);
    throw error;
  }
};

export const deleteCoupon = async (couponId: string) => {
  const response = await adminApi.delete(`/admin/coupons/${couponId}`);
  return response.data;
};

export const validateCoupon = async (code: string, amount: number) => {
  const response = await api.post('/coupons/validate', { code, amount });
  return response.data;
};

// Public endpoints (no authentication required)
export const getPromoBannerStatus = async () => {
  const response = await api.get('/promo-banner');
  return response.data;
};

// Public Settings (tax_rate, deposit_percentage) - for booking page
export const getPublicSettings = async () => {
  const response = await api.get('/admin/public-settings');
  return response.data;
};

// General Settings (tax_rate, deposit_percentage, etc.) - for admin
export const getSettings = async () => {
  const response = await adminApi.get('/admin/general-settings');
  return response.data;
};

export const updateSettings = async (settingsData: any) => {
  const response = await adminApi.patch('/admin/general-settings', settingsData);
  return response.data;
};

// Stripe
export const getStripeConfig = async () => {
  const response = await api.get('/stripe/config');
  return response.data;
};

export const createPaymentIntent = async (data: {
  amount: number;
  currency: string;
  customer_email: string;
  customer_name: string;
  booking_metadata: any;
}) => {
  const response = await api.post('/stripe/create-payment-intent', data);
  return response.data;
};

export const createOrGetPaymentLinkPaymentIntent = async (paymentLinkId: string, data: {
  amount: number;
  currency: string;
  customer_email: string;
  customer_name: string;
  booking_metadata: any;
}) => {
  const response = await api.post(`/stripe/payment-link/${paymentLinkId}/payment-intent`, data);
  return response.data;
};

export const getPaymentLinkStatus = async (paymentLinkId: string) => {
  const response = await api.get(`/stripe/payment-link/${paymentLinkId}`);
  return response.data;
};

export const updatePaymentLinkStatus = async (bookingId: string) => {
  const response = await api.post(`/stripe/update-payment-link-status/${bookingId}`);
  return response.data;
};

export const markPaymentLinkPaid = async (paymentLinkId: string, paymentIntentId: string) => {
  const response = await api.post(`/stripe/mark-payment-link-paid/${paymentLinkId}`, null, {
    params: { payment_intent_id: paymentIntentId }
  });
  return response.data;
};

export const getPaymentLinkData = async (uuid: string) => {
  const response = await api.get(`/stripe/payment-link-data/${uuid}`);
  return response.data;
};

export const deletePaymentLink = async (uuid: string) => {
  const response = await api.delete(`/stripe/payment-link/${uuid}`);
  return response.data;
};

// Reserved Dates
export const getReservedDates = async (startDate: string, endDate: string, productIds?: string) => {
  const params: any = {
    start_date: startDate,
    end_date: endDate
  };
  
  if (productIds) {
    params.product_ids = productIds;
  }
  
  const response = await api.get('/reserved-dates', { params });
  return response.data;
};

export const getReservedDatesForProduct = async (productId: string, startDate: string, endDate: string) => {
  const response = await api.get(`/reserved-dates/product/${productId}`, {
    params: {
      start_date: startDate,
      end_date: endDate
    }
  });
  return response.data;
};

export const getBlockedDatesForProduct = async (productId: string, startDate: string, endDate: string) => {
  const response = await api.get(`/blocked-dates/product/${productId}`, {
    params: {
      start_date: startDate,
      end_date: endDate
    }
  });
  return response.data;
};

// Address Autocomplete
export const autocompleteAddress = async (query: string, limit: number = 5) => {
  const response = await api.get('/address/autocomplete', {
    params: { q: query, limit }
  });
  return response.data;
};

export const lookupAddress = async (id: string) => {
  const response = await api.get('/address/lookup', {
    params: { id }
  });
  return response.data;
};

export const autosuggestAddress = async (query: string, limit: number = 5) => {
  const response = await api.get('/address/autosuggest', {
    params: { q: query, limit }
  });
  return response.data;
};

// Delivery
export const getDeliveryQuote = async (deliveryAddress: {
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postal_code: string;
}): Promise<DeliveryQuote> => {
  const response = await api.post('/delivery/quote', { delivery_address: deliveryAddress });
  return response.data;
};

export const getDeliveryConfig = async () => {
  const response = await api.get('/delivery/config');
  return response.data;
};

// Booking Statistics
export const getYearlyStats = async (year: number) => {
  const response = await adminApi.get('/bookings/stats/yearly', {
    params: { year }
  });
  return response.data;
};

// Blocked Dates
export const getBlockedDates = async () => {
  const response = await adminApi.get('/blocked-dates/');
  return response.data;
};

export const createBlockedDate = async (date: string, reason: string, product_ids?: string[]) => {
  const response = await adminApi.post('/blocked-dates/', { date, reason, product_ids });
  return response.data;
};

export const deleteBlockedDate = async (blockedDateId: string) => {
  const response = await adminApi.delete(`/blocked-dates/${blockedDateId}`);
  return response.data;
};

// Export API key management functions
export const refreshApiKey = async (): Promise<string> => {
  localStorage.removeItem('apiKey'); // Clear cache
  return API_KEY;
};

export const getApiKey = (): string => {
  return API_KEY;
};

export default api;
